import React, {
    useContext, useCallback, useState, useEffect
} from 'react';
import _ from 'lodash';
import { useTranslator } from '@jutro/locale';
import { ViewModelServiceContext, ViewModelForm } from '@xengage/gw-portals-viewmodel-react';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { convertToSentenceCase } from 'ml-portals-util-js';
import PropTypes from 'prop-types';
import appConfig from 'app-config';

import metadata from './RetrieveQuote.metadata.json5';
import styles from './RetrieveQuote.module.scss';
import messages from './RetrieveQuote.messages';

const HVQ_QUOTEID_PREFIX = 'HVQ';

function RetrieveQuote(props) {
    const { onTogglePageState, onRetrieveQuote, quoteIDSearch, history, inputPostalCode } = props;
    const [retrieveQuoteVM, updateRetrieveQuoteVM] = useState({});
    const { onValidate, isComponentValid } = useValidation('RetrieveQuote');
    const [showErrors, setShowErrors] = useState(false);
    const viewModelService = useContext(ViewModelServiceContext);
    const translator = useTranslator();
    const isQnB = appConfig.portalCode === 'QB';

    useEffect(
        () => {
            const fromToggleRetrieveQuote = _.get(history, 'location.state.toggleRetrieveQuote');
            const postalCode = fromToggleRetrieveQuote ? inputPostalCode : _.get(history, 'location.state.postalCode');
            try {
                const retrieveQuoteViewModel = viewModelService.create(
                    { retrieveType_ML: 'quoteID', quoteID: quoteIDSearch || localStorage.getItem('quoteID'), postalCode: postalCode },
                    'pc',
                    'edge.capabilities.quote.submission.dto.QuoteRetrievalDTO'
                );
                updateRetrieveQuoteVM(retrieveQuoteViewModel);
            } catch (error) {
                console.error(error);
                _.set(history, 'location.state.retries', 0);
                onTogglePageState();
                history.push('/', _.get(history, 'location.state'));
            }
        },
        // Disabling as this its called when a parent is re rendered
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const writeValue = useCallback(
        (value, path) => {
            const newRetrieveQuoteVM = viewModelService.clone(retrieveQuoteVM);
            _.set(newRetrieveQuoteVM, path, value);
            updateRetrieveQuoteVM(newRetrieveQuoteVM);
        },
        [retrieveQuoteVM, updateRetrieveQuoteVM, viewModelService]
    );

    const retrieveQuoteContinueButtonLabel = !_.isNil(localStorage.getItem("quoteID"))
        ? translator(messages.mlContinueMyQuote)
        : translator(messages.continue);

    const overrideProps = {
        retrieveQuoteSubmissionIDInput: {
            visible: _.get(retrieveQuoteVM, 'retrieveType_ML.value') === 'quoteID'
        },
        retrieveQuoteEmailInput: {
            visible: _.get(retrieveQuoteVM, 'retrieveType_ML.value') === 'email'
        },
        startNewQuoteButton: {
            visible: !!localStorage.getItem('quoteID'),
            className: isQnB ? "secondaryButton widthSmall" : "",
            content: isQnB ? convertToSentenceCase(translator(messages.mlStartANewQuote)) : translator(messages.mlStartANewQuote)
        },
        retrieveQuoteContinueButton: {
            content: isQnB ? convertToSentenceCase(retrieveQuoteContinueButtonLabel) : retrieveQuoteContinueButtonLabel,
            className: isQnB ? "quoteButtonNoBorderQB primaryButton" : "quoteButtonNoBorderQB"
        },
        welcomeBackTitle: {
            content: translator(messages.welcomeBack, {name: localStorage.getItem('firstName')}),
            className: isQnB ? "page-header vertical-spacing-10 headerOne" : "page-header vertical-spacing-10"
        },
        welcomeBackContainer: {
            visible: !!localStorage.getItem('quoteID')
        },
        retrieveQuoteTitle: {
            visible: !localStorage.getItem('quoteID'),
            className: isQnB ? "page-header vertical-spacing-10 headerOne" : "page-header vertical-spacing-10"
        },
        retrieveType: {
            className: isQnB ? "retrieveType radioButtonQnB" : "retrieveType"
        }
    };

    const changeRetrieveType = (value) => {
        const newRetrieveQuoteVM = viewModelService.clone(retrieveQuoteVM);
        _.set(newRetrieveQuoteVM, 'retrieveType_ML.value', value);
        updateRetrieveQuoteVM(newRetrieveQuoteVM);
    };

    const isHvqQuoteId = () => {
        const quoteID = retrieveQuoteVM.quoteID.value;
        return quoteID && quoteID.toUpperCase().startsWith(HVQ_QUOTEID_PREFIX);
    };

    const retrieveQuote = () => {
        if (isComponentValid) {
            onRetrieveQuote(retrieveQuoteVM.value, isHvqQuoteId());
        } else {
            setShowErrors(true);
        }
    };

    const goToNewQuote = () => {
        if (isComponentValid) {
            onTogglePageState();
        } else {
            setShowErrors(true);
        }
    };

    const resolvers = {
        resolveClassNameMap: styles,
        resolveCallbackMap: {
            onTogglePageState: onTogglePageState,
            onRetrieveQuote: retrieveQuote,
            onChangeRetrieveType: changeRetrieveType,
            onStartNewQuoteClicked: goToNewQuote
        }
    };

    return (
        <ViewModelForm
            overrideProps={overrideProps}
            uiProps={metadata.componentContent}
            model={retrieveQuoteVM}
            onValueChange={writeValue}
            classNameMap={resolvers.resolveClassNameMap}
            callbackMap={resolvers.resolveCallbackMap}
            onValidationChange={onValidate}
            showErrors={showErrors}
        />
    );
}

RetrieveQuote.propTypes = {
    onTogglePageState: PropTypes.func.isRequired,
    onRetrieveQuote: PropTypes.func.isRequired
};

export default RetrieveQuote;
